import Button from 'Components/Buttons/Button';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow, { WindowSize } from 'Components/OverlayWindow';
import { useClosePopup, useCurrentPopup } from 'Components/Popups';
import { IDirectoryNode, useRestoreMutation } from 'app/services/documents';
import { IProject } from 'app/services/responseTypes';
import { ITask } from 'app/services/tasks';
import { IUser } from 'app/services/user';
import { useState } from 'react';
import { useAuth } from 'utils/AuthProvider';
import { useDocumentsContext } from '.';
import { FolderSelector } from './MoveModal';
import { firstCommonParent } from './MultiNodeActions';

export interface MoveModalProps {
  toBeMoved: IDirectoryNode[];
  lockClient?: IUser;
  lockTask?: ITask;
  lockProject?: IProject;
  onMove: () => void;
}
export const RestoreModal = () => {
  const closePopup = useClosePopup();
  const { selected } = useDocumentsContext();
  const currentPopup = useCurrentPopup<IDirectoryNode[]>();

  // can select multiple but selected one item from the action popup
  const toBeMoved =
    Object.values(selected).length > 0
      ? Object.values(selected)
      : currentPopup.contextData || [];

  const {
    lockTask,
    lockProject,
    lockClient,
    setFilters,
    filters,
    unselectAll,
  } = useDocumentsContext();
  const { user: loggedInUser, isClient } = useAuth();

  const [RestoreNode, { isLoading }] = useRestoreMutation();

  const [currentFolder, setCurrentFolder] = useState<IDirectoryNode>();

  const currentFolderId = currentFolder?.id;

  const successAndClose = (message: string) => {
    successToast(message);
    currentPopup.unlock();
    closePopup();
  };

  const errorAndClose = (message: string) => {
    errorToast(message);
    currentPopup.unlock();
    closePopup();
  };

  const Restore = () => {
    currentPopup.disableBackdrop();
    currentPopup.lock();
    const promises = toBeMoved
      //   .filter((d) => (d.parent_id || undefined) !== currentFolderId)
      .map((node) =>
        RestoreNode({
          id: node.id,
          destination_parent_id: currentFolderId || null,
        }).unwrap(),
      );

    Promise.allSettled(promises).then((results) => {
      const successes = results.filter(
        (result) => result.status === 'fulfilled',
      );
      const failures = results.filter((result) => result.status === 'rejected');
      if (successes.length > 0) {
        unselectAll();
        setFilters({
          ...filters,
          id: firstCommonParent(toBeMoved),
          is_trashed: true,
        });
        successAndClose(`${successes.length} items restored`);
      }
      if (failures.length > 0) {
        errorAndClose(`${failures.length} items failed to be restored`);
      }
    });
  };

  return (
    <OverlayWindow
      size={WindowSize.lg + ' h-[40rem]'}
      paddingSize=""
      children={
        <FolderSelector
          showSelect={false}
          clientId={isClient ? loggedInUser?.id : lockClient?.id}
          taskId={lockTask?.id}
          projectId={lockProject?.id}
          parentId={undefined}
          onChange={setCurrentFolder}
        >
          <div className="text-xs text-gray-lighter">
            {'Restoring '}
            <span className="font-semibold">
              {toBeMoved.length === 1
                ? toBeMoved[0].title
                : `${toBeMoved.length} items`}
            </span>
            {!isLoading && (
              <>
                {' to '}
                <span className="font-semibold">
                  {currentFolder?.id ? currentFolder?.title : 'Home'}
                </span>
              </>
            )}
          </div>
        </FolderSelector>
      }
      footer={
        <>
          <div className="flex w-full  gap-4 ">
            <Button
              theme="gray-outline"
              disabled={isLoading}
              onClick={closePopup}
            >
              Cancel
            </Button>

            <Button
              theme="green-fill"
              disabled={isLoading}
              isLoading={isLoading}
              onClick={Restore}
            >
              Restore Here
            </Button>
          </div>
        </>
      }
    />
  );
};
